import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { logService } from '../lib/logService';

/**
 * Hook genérico para ler settings da tabela `platform_settings` com realtime.
 *
 * Story mobile-builder.5 — Generalização do pattern usado em
 * `useMarketingSettings` (per-setting), mas com realtime subscription pra
 * reagir a updates do admin sem reload.
 *
 * @param key — chave em platform_settings.key (ex: 'mobile_builder_enabled')
 * @returns T | null — null enquanto loading OU se row ausente
 *
 * RPC: `get_platform_setting(p_key: text)` retorna o JSONB value diretamente.
 *
 * Realtime: usa supabase.channel + filter `key=eq.{key}` pra receber só
 * mudanças relevantes. Cleanup em unmount é mandatório (channel.unsubscribe).
 */
export function usePlatformSetting<T>(key: string): T | null {
  const [value, setValue] = useState<T | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchValue = async () => {
      try {
        const { data, error } = await supabase.rpc('get_platform_setting', { p_key: key });
        if (error) {
          logService.error('platformSetting.fetch', error.message, { key, supabase_code: error.code });
          return;
        }
        if (isMounted && data !== null && data !== undefined) {
          setValue(data as T);
        }
      } catch (err) {
        logService.error(
          'platformSetting.fetch',
          err instanceof Error ? err.message : 'Unknown error',
          { key, error: err as Error },
        );
      }
    };

    fetchValue();

    const channel = supabase
      .channel(`platform_settings:${key}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'platform_settings',
          filter: `key=eq.${key}`,
        },
        (payload: { new: { value?: unknown } | null; eventType: string }) => {
          if (!isMounted) return;
          if (payload.eventType === 'DELETE') {
            setValue(null);
            return;
          }
          if (payload.new && 'value' in payload.new && payload.new.value !== undefined) {
            setValue(payload.new.value as T);
          }
        },
      )
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, [key]);

  return value;
}
